import { resolveSectionSwitchPath } from '~/utils/resolveSectionSwitchPath';

export const useCountryLanguageSwitch = () => {
    const { countryCookie, languageCookie } = useCountryLanguageCookies();
    const { emitLangSwitch } = useLangSwitchBus();
    const { $subSectionRegistry } = useNuxtApp();
    const countryLanguages = useCountryLanguages();

    const route = useRoute();
    const matchedConfig = useCurrentSectionConfig();

    const commitCountryLanguage = async (countryCode: string, langCode: string) => {
        const currentCountryCode = useLanguage().country.value;
        const didCountryChange = currentCountryCode !== countryCode;
        const router = useRouter();
        const fullPathBeforeLangSwitch = router.currentRoute.value.fullPath;
        const targetCountryConfig = countryLanguages[countryCode];
        if (!targetCountryConfig) return;

        const targetLanguage = targetCountryConfig.availableLanguages.includes(langCode)
            ? langCode
            : targetCountryConfig.defaultLanguage;
        const localizedStoryPath = didCountryChange ? null : await emitLangSwitch(targetLanguage, countryCode);
        const sectionSwitchPath = resolveSectionSwitchPath(
            $subSectionRegistry.value,
            matchedConfig.value?.sectionId,
            countryCode,
            targetLanguage,
            countryLanguages
        );
        const targetPath = localizedStoryPath ?? `${sectionSwitchPath}${getAccountRouteSuffix(route.path)}`;

        countryCookie.value = countryCode;
        languageCookie.value = targetLanguage;

        // A page-level lang-switch listener may already have navigated to a localized slug.
        // In that case we must skip the generic fallback navigation to avoid transient wrong-route hops.
        if (router.currentRoute.value.fullPath !== fullPathBeforeLangSwitch) {
            return;
        }

        const target = { path: targetPath, query: route.query, hash: route.hash };
        if (router.resolve(target).fullPath !== route.fullPath) {
            log.debug(
                `[useCountryLanguageSwitch] Navigating to target path: ${target.path} with query: ${JSON.stringify(target.query)}`
            );
            await navigateTo(target, { replace: true });
        }
    };

    return { commitCountryLanguage };
};

export function getAccountRouteSuffix(path: string): string {
    return /\/account(?:\/.+)?$/.exec(path)?.[0] ?? '';
}
